import {
  ReasonsWithChildren,
  InstructionsBanner,
  ReasonsWithoutChildren
} from '@/ui/components/reasons'
import { DashboardReason, ReturnReason } from '@/domain/return-reasons'
import {
  useReturnReasons,
  useSaveReturnReasons
} from '@/application/manage-return-reasons'
import {
  useReturnConditions,
  useSaveReturnConditions
} from '@/application/manage-return-conditions'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { LoadError, NavigationTabs, PageHeader, SaveBar } from '@/ui/shared'
import { useReturnConditionsTabs } from './configuration-tabs'
import { deepCopy, equalJSON } from '@/utils'
import { Base, ReverCard, ReverLoading } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'

export default function Reasons() {
  const { t } = useTranslation()
  const { isFlagEnabled } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'configuration'
  })
  const {
    returnReasons,
    setReturnReasons,
    originalReturnReasons,
    loadingReturnReasons,
    returnReasonsFailed,
    reloadReturnReasons
  } = useReturnReasons()

  const { callSaveReturnReasons, loadingSaveReturnReasons } =
    useSaveReturnReasons()

  const { originalReturnConditions } = useReturnConditions()
  const { callSaveReturnConditions } = useSaveReturnConditions()

  const handleToggleRandomizeOrder = (checked: boolean) => {
    callSaveReturnConditions(originalReturnConditions, {
      ...originalReturnConditions,
      randomizeReturnReasonsOrder: checked
    })
  }

  const reasonsWithChildren: DashboardReason[] = returnReasons
    .map((reason, index) => {
      return { ...reason, index }
    })
    .filter((reason) => reason.subReasons.length > 0)
  const reasonsWithoutChildren: DashboardReason[] = returnReasons
    .map((reason, index) => {
      return { ...reason, index }
    })
    .filter((reason) => reason.subReasons.length === 0)

  const handleChangeMandatoryComment = (
    value: boolean,
    reasonIndex: number,
    subReasonIndex?: number
  ) => {
    const newReturnReasons = [...returnReasons]
    if (subReasonIndex === undefined) {
      newReturnReasons[reasonIndex].parentReason.mandatoryComment = value
    } else {
      newReturnReasons[reasonIndex].subReasons[
        subReasonIndex
      ].mandatoryComment = value
    }
    setReturnReasons(newReturnReasons)
  }

  const handleChangeCategoryIds = (
    categoryIds: string[],
    reasonIndex: number,
    subReasonIndex?: number
  ) => {
    const newReturnReasons = [...returnReasons]
    if (subReasonIndex === undefined) {
      newReturnReasons[reasonIndex].parentReason.categoryIds = categoryIds
      setReturnReasons(newReturnReasons)
      return
    }

    newReturnReasons[reasonIndex].subReasons[subReasonIndex].categoryIds =
      categoryIds
    setReturnReasons(newReturnReasons)
  }

  const isShopify =
    useAppSelector((s) => s.settingsApi.settings.response.platform) ===
    'SHOPIFY'

  const role = useAppSelector((s) => s.userApi.getMe.response.user?.role)
  const canEdit = role === Roles.ReadWrite

  const handleClickDiscard = () => {
    setReturnReasons(deepCopy(originalReturnReasons))
  }

  const handleClickSave = () => {
    callSaveReturnReasons(returnReasons)
  }

  const changes = countChangedReasons(returnReasons, originalReturnReasons)

  const tabs = useReturnConditionsTabs()

  if (loadingReturnReasons) return <ReverLoading />
  // Saving the set replaces every reason, so it is never edited without the stored one.
  if (returnReasonsFailed)
    return (
      <>
        <NavigationTabs tabs={tabs} selectedIndex={1} />
        <PageHeader
          title={t('return_reasons.title')}
          description={t('return_reasons.description')}
        />
        <div className="max-w-[1000px] px-7 py-4">
          <LoadError
            message={t('return_reasons.load_error')}
            onRetry={reloadReturnReasons}
          />
        </div>
      </>
    )
  return (
    <>
      <NavigationTabs tabs={tabs} selectedIndex={1} />
      <PageHeader
        title={t('return_reasons.title')}
        description={t('return_reasons.description')}
      />
      <div className="flex flex-col gap-4 px-7 py-4">
        {isFlagEnabled && <BlockedDashboardOverlay />}
        {isShopify ? (
          <div className="max-w-[1000px]">
            <InstructionsBanner />
          </div>
        ) : null}
        <div className="max-w-[1000px]">
          <ReverCard className="p-4" fullWidth>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold">
                  {t('return_reasons.randomize_order')}
                </p>
                <p className="text-xs text-neutral-600">
                  {t('return_reasons.randomize_order_description')}
                </p>
              </div>
              <Base.Switch
                aria-label={t('return_reasons.randomize_order')}
                disabled={!canEdit}
                checked={originalReturnConditions.randomizeReturnReasonsOrder}
                onCheckedChange={handleToggleRandomizeOrder}
              />
            </div>
          </ReverCard>
        </div>
        <div className="max-w-[1000px]">
          <ReasonsWithoutChildren
            reasons={reasonsWithoutChildren}
            handleChangeCategoryIds={handleChangeCategoryIds}
            handleChangeMandatoryComment={handleChangeMandatoryComment}
            readOnly={!canEdit}
            isShopify={isShopify}
          />

          {reasonsWithChildren.map((reason, i) => {
            return (
              <ReasonsWithChildren
                key={i}
                index={reason.index}
                parentReason={reason.parentReason}
                reasons={reason.subReasons}
                handleChangeCategoryIds={handleChangeCategoryIds}
                handleChangeMandatoryComment={handleChangeMandatoryComment}
                readOnly={!canEdit}
                isShopify={isShopify}
              />
            )
          })}
        </div>
        <SaveBar
          editable={canEdit}
          changes={changes}
          saving={loadingSaveReturnReasons}
          onDiscard={handleClickDiscard}
          onSave={handleClickSave}
        />
      </div>
    </>
  )
}

// Counts each reason or sub-reason whose settings differ from the stored ones.
function countChangedReasons(edited: ReturnReason[], stored: ReturnReason[]) {
  return edited
    .flatMap((reason, i) => [
      [reason.parentReason, stored[i]?.parentReason],
      ...reason.subReasons.map((sub, j) => [sub, stored[i]?.subReasons[j]])
    ])
    .filter(([a, b]) => !equalJSON(a, b)).length
}
